import { useMemo } from "react";
import { calculateMonthlyPayment } from "@lending/pages/PersonalLoan/Offers/utils/loanCalculations";
import { PersonalLoanOffer } from "@lending/types";

function useLoanCalculator(principal: number, lendingOffers: PersonalLoanOffer[]) {
  // Formula for calculating monthly payment
  // PMT = P * (r * (1 + r)^n) / ((1 + r)^n - 1)
  //
  // Where:
  // PMT = Monthly Payment
  // P = Principal
  // r = Monthly Interest Rate (Annual Rate / 12)
  // n = Total Number of Months
  //
  // calculateMonthlyPayment is linear in principal, so the returned monthly
  // payment is in the same unit as `principal`. We pass cents in and get cents
  // out so callers can format with `formatCentsAsDollarsAndCents` consistently.

  const calculatedOffers = useMemo(() => {
    return lendingOffers.map((offer) => {
      const monthlyPayment = calculateMonthlyPayment(
        principal,
        offer.apr_basis / 100,
        offer.term_months
      );

      // Intentionally overwrites the backend-provided monthly_payment.
      // The user may adjust their requested principal via the slider, so we
      // recalculate the payment client-side for the chosen amount.
      return {
        ...offer,
        monthly_payment: monthlyPayment,
      };
    });
  }, [principal, lendingOffers]);

  return {
    offers: calculatedOffers,
    max_apr: 18,
  };
}

export { useLoanCalculator };
